import { OfferCounterInfo, OfferState, OfferType } from '@/types/offer';
import { ClientPoolStatus, PoolOffer, PoolStatus } from '@/types/pool';
import { UserClientRole } from '@/types/user';

export type StatusConfig = {
  label: {
    [UserClientRole.INVESTOR]: string;
    [UserClientRole.BORROWER]: string;
  };
  bgColor: string;
  dotColor: string;
  padding: string;
  fontSize: string;
};

const statusConfigs: Record<PoolStatus | ClientPoolStatus, StatusConfig> = {
  [PoolStatus.DRAFT]: {
    label: {
      [UserClientRole.INVESTOR]: 'Draft',
      [UserClientRole.BORROWER]: 'Draft',
    },
    bgColor: 'bg-[#F4F4F4]',
    dotColor: 'bg-[#767676]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.SYSTEM_PENDING]: {
    label: {
      [UserClientRole.INVESTOR]: 'System Pending',
      [UserClientRole.BORROWER]: 'System Pending',
    },
    bgColor: 'bg-[#F7E6D6]',
    dotColor: 'bg-[#DBAC7E]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.AWAITING_INVESTORS]: {
    label: {
      [UserClientRole.INVESTOR]: 'Available to invest',
      [UserClientRole.BORROWER]: 'Awaiting Investors',
    },
    bgColor: 'bg-[#CBD764]',
    dotColor: 'bg-[#7E8C03]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.OFFER_RECEIVED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Offer sent',
      [UserClientRole.BORROWER]: 'Offer Received',
    },
    bgColor: 'bg-[#A99AFC]',
    dotColor: 'bg-[#4A3E8F]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.IN_ESCROW]: {
    label: {
      [UserClientRole.INVESTOR]: 'In Escrow',
      [UserClientRole.BORROWER]: 'In Escrow',
    },
    bgColor: 'bg-[#74BBBA]',
    dotColor: 'bg-[#558786]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.CLOSED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Closed',
      [UserClientRole.BORROWER]: 'Closed',
    },
    bgColor: 'bg-[#D6D6D6]',
    dotColor: 'bg-[#784B1B]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.CANCELED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Canceled',
      [UserClientRole.BORROWER]: 'Canceled',
    },
    bgColor: 'bg-[#F3F4F6]',
    dotColor: 'bg-[#6B7280]',
    padding: 'px-3 py-1.5',
    fontSize: 'text-xs',
  },
  [PoolStatus.FUNDING_REJECTED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Funding Rejected',
      [UserClientRole.BORROWER]: 'Funding Rejected',
    },
    bgColor: 'bg-[#FEE2E2]',
    dotColor: 'bg-[#DC2626]',
    padding: 'px-3 py-1.5',
    fontSize: 'text-xs',
  },
  [PoolStatus.REJECTED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Rejected',
      [UserClientRole.BORROWER]: 'Rejected',
    },
    bgColor: 'bg-[#E7D8D8]',
    dotColor: 'bg-[#CC4747]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [PoolStatus.FUNDED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Escrow Closed - Pool Funded',
      [UserClientRole.BORROWER]: 'Escrow Closed - Pool Funded',
    },
    bgColor: 'bg-[#DDF4E6]',
    dotColor: 'bg-[#65CC8E]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [ClientPoolStatus.COUNTER_OFFER_RECEIVED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Counter offer sent',
      [UserClientRole.BORROWER]: 'Counter offer',
    },
    bgColor: 'bg-[#EED894]',
    dotColor: 'bg-[#BC9C38]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [ClientPoolStatus.ESCROW_DOCUMENTATION_COMPLETE]: {
    label: {
      [UserClientRole.INVESTOR]: 'Escrow Documentation Collection Complete',
      [UserClientRole.BORROWER]: 'Escrow Documentation Collection Complete',
    },
    bgColor: 'bg-[#518685]',
    dotColor: 'bg-[#375B5B]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [ClientPoolStatus.INVESTOR_COUNTER_OFFER_ACCEPTED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Counter offer accepted',
      [UserClientRole.BORROWER]: 'Counter offer accepted',
    },
    bgColor: 'bg-[#9D175A]',
    dotColor: 'bg-[#E94478]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
  [ClientPoolStatus.BORROWER_OFFER_ACCEPTED]: {
    label: {
      [UserClientRole.INVESTOR]: 'Offer accepted',
      [UserClientRole.BORROWER]: 'Offer accepted',
    },
    bgColor: 'bg-[#9D175A]',
    dotColor: 'bg-[#E94478]',
    padding: 'px-2.5 py-1',
    fontSize: 'text-sm',
  },
};

/**
 * Get status configuration for a pool status
 * @param status - The pool status
 * @returns Status configuration with labels for both investor and borrower modes
 */
export const getPoolStatusConfig = (status: PoolStatus | ClientPoolStatus): StatusConfig => {
  return statusConfigs[status] || statusConfigs[PoolStatus.SYSTEM_PENDING];
};

/**
 * Get status label for a pool status based on view mode
 * @param status - The pool status
 * @param mode - View mode: 'investor' or 'borrower'
 * @returns Status label appropriate for the view mode
 */
export const getPoolStatusLabel = (
  status: PoolStatus | ClientPoolStatus,
  mode: UserClientRole = UserClientRole.BORROWER
): string => {
  const config = getPoolStatusConfig(status);
  return config.label[mode];
};

/**
 * Get all status configurations
 */
export const getAllStatusConfigs = (): Record<PoolStatus | ClientPoolStatus, StatusConfig> => {
  return statusConfigs;
};

export const hasCounterOffer = (offers: PoolOffer[] | null): boolean => {
  if (!offers) return false;
  return offers.some((offer) => offer.type === OfferType.COUNTER_OFFER && offer.state === OfferState.SENT);
};

// TODO: this should get index of the counter offer
export const getAcceptedCounterOfferInfo = (offers: PoolOffer[] | null): OfferCounterInfo | null => {
  if (!offers) return null;
  return (
    offers.find((offer) => offer.state === OfferState.ACCEPTED && offer.type === OfferType.COUNTER_OFFER)
      ?.counter_info || null
  );
};

export const getAvailableCounterOffers = (offers: PoolOffer[] | null): PoolOffer[] => {
  if (!offers) return [];
  return offers
    .filter((offer) => offer.state === OfferState.SENT && offer.type === OfferType.COUNTER_OFFER)
    .filter((offer) => offer.counter_info !== null);
};

export const getPoolStatus = (
  status: PoolStatus | ClientPoolStatus,
  hasCounterOffer: boolean
): PoolStatus | ClientPoolStatus => {
  if (hasCounterOffer && status === PoolStatus.AWAITING_INVESTORS) return ClientPoolStatus.COUNTER_OFFER_RECEIVED;
  return status;
};
